"use client";

import { c as _c } from "react/compiler-runtime";
import { ADD_ONESIGNAL_SUBSCRIPTION_MUTATION } from "@/gql/one-signal";
import { useAuth } from "@/hooks/use-auth";
import { logger } from "@/utils/logger";
import { useMutation } from "@apollo/client";
import Script from "next/script";
import { useEffect, useState } from "react";
import { toast } from "sonner";
const ONESIGNAL_APP_ID = "b32d0c69-01f0-46d5-ad0e-2ff24378ca0b";
const SAFARI_WEB_ID = "web.onesignal.auto.35c3b21f-3634-4ed2-bd52-fd09e2637415";
const PROMPT_DELAY_MS = 20_000; // 20 seconds delay
const TOAST_STORAGE_KEY = "onesignal_toast_dismissed";
interface OneSignalNotifications {
  requestPermission: () => Promise<void>;
  permission: boolean;
}
interface OneSignalSDK {
  init: (config: {
    appId: string;
    safari_web_id: string;
    notifyButton: {
      enable: boolean;
    };
    allowLocalhostAsSecureOrigin?: boolean;
    serviceWorkerParam?: {
      scope: string;
    };
    serviceWorkerPath?: string;
  }) => Promise<void>;
  login: (externalId: string) => Promise<void>;
  logout: () => Promise<void>;
  Notifications: OneSignalNotifications;
  User: {
    PushSubscription: {
      id: string | null | undefined;
      optedIn: boolean;
    };
  };
}
declare global {
  interface Window {
    OneSignalDeferred?: Array<(OneSignal: OneSignalSDK) => Promise<void>>;
  }
}
export function OneSignalInit() {
  const $ = _c(16);
  const [shouldInit, setShouldInit] = useState(false);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [addSubscription] = useMutation(ADD_ONESIGNAL_SUBSCRIPTION_MUTATION);
  const {
    user
  } = useAuth();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (typeof window === "undefined") {
        return;
      }
      if (!("Notification" in window)) {
        return;
      }
      if (Notification.permission !== "default") {
        return;
      }
      if (sessionStorage.getItem(TOAST_STORAGE_KEY)) {
        return;
      }
      window.OneSignalDeferred = window.OneSignalDeferred || [];
      const timer = setTimeout(() => {
        setShouldInit(true);
      }, PROMPT_DELAY_MS);
      return () => clearTimeout(timer);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[2] !== shouldInit) {
    t2 = () => {
      if (!shouldInit) {
        return;
      }
      window.OneSignalDeferred = window.OneSignalDeferred || [];
      window.OneSignalDeferred.push(async function (OneSignal) {
        ;
        try {
          await OneSignal.init({
            appId: ONESIGNAL_APP_ID,
            safari_web_id: SAFARI_WEB_ID,
            notifyButton: {
              enable: false
            },
            allowLocalhostAsSecureOrigin: process.env.NODE_ENV === "development",
            serviceWorkerParam: {
              scope: "/"
            },
            serviceWorkerPath: "sw.js"
          });
          setSdkLoaded(true);
        } catch (t4) {
          const error = t4;
          if (!error?.toString().includes("Can only be used on: https://settlin.in")) {
            logger.debug("[OneSignal] Initialization failed (non-critical - app works without notifications):", error);
          }
        }
      });
    };
    t3 = [shouldInit];
    $[2] = shouldInit;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== sdkLoaded || $[6] !== user) {
    t4 = () => {
      if (!sdkLoaded) {
        return;
      }
      window.OneSignalDeferred?.push(async OneSignal_0 => {
        if (user?.id) {
          await OneSignal_0.login(user.id);
        } else {
          await OneSignal_0.logout();
        }
      });
    };
    $[5] = sdkLoaded;
    $[6] = user;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = user?.id;
  let t6;
  if ($[8] !== sdkLoaded || $[9] !== t5) {
    t6 = [sdkLoaded, t5];
    $[8] = sdkLoaded;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  useEffect(t4, t6);
  let t7;
  let t8;
  if ($[11] !== addSubscription || $[12] !== sdkLoaded) {
    t7 = () => {
      if (!sdkLoaded) {
        return;
      }
      const timer_0 = setTimeout(() => {
        toast("\uD83D\uDD14 Stay notified!", {
          description: "Get instant notifications about your property visits and updates",
          duration: 15000,
          action: {
            label: "Enable",
            onClick: () => {
              window.OneSignalDeferred?.push(async OneSignal_1 => {
                try {
                  await OneSignal_1.Notifications.requestPermission();
                  if (OneSignal_1.Notifications.permission) {
                    const subscriptionId = OneSignal_1.User.PushSubscription.id;
                    if (subscriptionId) {
                      await addSubscription({
                        variables: {
                          params: {
                            addSubscriptionId: {
                              oneSignalPlayerId: subscriptionId,
                              oneSignalAppId: ONESIGNAL_APP_ID
                            }
                          }
                        }
                      });
                    }
                  }
                } catch {}
              });
            }
          },
          onDismiss: _temp
        });
      }, 500);
      return () => clearTimeout(timer_0);
    };
    t8 = [addSubscription, sdkLoaded];
    $[11] = addSubscription;
    $[12] = sdkLoaded;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  useEffect(t7, t8);
  if (!shouldInit) {
    return null;
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Script src="https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js" strategy="afterInteractive" onError={_temp2} />;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}
function _temp2(e) {
  logger.debug("[OneSignal] SDK script failed to load (non-critical):", e);
}
function _temp() {
  sessionStorage.setItem(TOAST_STORAGE_KEY, "true");
}